import {
  promiseState,
  useBooleanState,
  useNavigator
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useState } from 'react';
import { viewTexts } from '../../../constants/view-texts';
import { useListFiltered } from '../../../functions/hooks/use-list-filtered';
import { koumutenList$ } from '../../../global-state/database/koumuten-list';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { routes } from '../../../routing/routes';
import { IKoumutenType } from '../../../types/record/koumuten';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { KoumutenListView } from './koumuten-list-view';

export const KoumutenList: FC<{}> = memo(() => {
  const navigator = useNavigator();

  const [
    createNewKoumutenDialogIsOpen,
    openCreateNewKoumutenDialog,
    closeCreateNewKoumutenDialog
  ] = useBooleanState(false);

  const [
    editKoumutenDialogIsOpen,
    openEditKoumutenDialog,
    closeEditKoumutenDialog
  ] = useBooleanState(false);

  const [
    deleteKoumutenDialogIsOpen,
    openDeleteKoumutenDialog,
    closeDeleteKoumutenDialog
  ] = useBooleanState(false);

  const [
    // dummy comment to control prettier
    koumutenToEdit,
    setKoumutenToEdit
  ] = useState<IKoumutenType | undefined>(undefined);

  const [
    // dummy comment to control prettier
    koumutenToDelete,
    setKoumutenToDelete
  ] = useState<IKoumutenType | undefined>(undefined);

  /* event handlers */

  const koumutenItemOnClick = useCallback(
    (koumutenId: number) => {
      navigator(routes.companyList(koumutenId));
    },
    [navigator]
  );

  const editKoumutenButtonOnClick = useCallback(
    (koumuten: IKoumutenType) => {
      setKoumutenToEdit(koumuten);
      openEditKoumutenDialog();
    },
    [setKoumutenToEdit, openEditKoumutenDialog]
  );

  const deleteKoumutenButtonOnClick = useCallback(
    (koumuten: IKoumutenType) => {
      setKoumutenToDelete(koumuten);
      openDeleteKoumutenDialog();
    },
    [setKoumutenToDelete, openDeleteKoumutenDialog]
  );

  const {
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    setRowsPerPage: onRowsPerPageChange,
    numRows,
    listFilteredCurrentPage: koumutenListFiltered
  } = useListFiltered(koumutenList$);

  useLoadingPage(koumutenListFiltered?.status === 'pending');

  return (
    <>
      {koumutenListFiltered === undefined ? (
        undefined
      ) : koumutenListFiltered.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.koumutenList} />
      ) : (
        <KoumutenListView
          searchString={searchString}
          onSearchStringChange={onSearchStringChange}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          numRows={numRows}
          koumutenListFiltered={koumutenListFiltered.value.latest}
          koumutenOnClick={koumutenItemOnClick}
          createNewKoumutenOnClick={openCreateNewKoumutenDialog}
          createNewKoumutenDialogOpen={createNewKoumutenDialogIsOpen}
          createNewKoumutenDialogOnClose={closeCreateNewKoumutenDialog}
          editKoumutenOnClick={editKoumutenButtonOnClick}
          editKoumutenDialogIsOpen={editKoumutenDialogIsOpen}
          editKoumutenDialogOnClose={closeEditKoumutenDialog}
          koumutenToEdit={koumutenToEdit}
          deleteKoumutenOnClick={deleteKoumutenButtonOnClick}
          deleteKoumutenDialogIsOpen={deleteKoumutenDialogIsOpen}
          deleteKoumutenDialogOnClose={closeDeleteKoumutenDialog}
          koumutenToDelete={koumutenToDelete}
        />
      )}
    </>
  );
});

KoumutenList.displayName = 'KoumutenList';
